// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { FormInputWrapper } from "@insite/mobius/FormField";
import Link, { LinkProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { css } from "styled-components";

type Props = {
    alphabet: LetterDetails[];
    prefix?: string;
    extendedStyles?: AlphabetNavigationStyles;
    onLetterClick?: (letter: string) => void;
    // XNG-Changes
    handleSearchFilterBrands?: (e: string) => void;
    searchFilterBrands?: string;
    selectedLetter?: string;
};

export interface LetterDetails {
    letter: string;
    linkable: boolean;
}

export interface AlphabetNavigationStyles {
    container?: InjectableCss;
    navigationItem?: InjectableCss;
    navigationLink?: LinkProps;
    navigationLinkText?: TypographyPresentationProps;
    navigationTextEmpty?: TypographyPresentationProps;
    // XNG-Changes
    alphabetContainer?: InjectableCss;
    titleText?: TypographyPresentationProps;
    brandsLettersWrapper?: InjectableCss;
    alphabetSection?: InjectableCss;
    filterSection?: InjectableCss;
    filterInput?: TextFieldProps;
    selectWrapper?: InjectableCss;
    select?: SelectPresentationProps;
}

export const alphabetNavigationStyles: AlphabetNavigationStyles = {
    container: {
        css: css`
            text-transform: uppercase;
            justify-items: center;
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
        `,
    },
    navigationItem: {
        css: css`
            margin: 0;
            display: inline-block;
            flex: 0 0 30px;
        `,
    },
    navigationLink: {
        css: css`
            display: block;
        `,
        typographyProps: {
            transform: "uppercase",
            variant: "headerTertiary",
            forwardAs: "span",
        },
    },
    navigationTextEmpty: {
        transform: "uppercase",
        variant: "headerTertiary",
        forwardAs: "span",
        color: "text.disabled",
    },
};

const StyledSpan = getStyledWrapper("span");

const AlphabetNavigation: FC<Props> = (props: Props) => {
    const [styles] = useState(() => mergeToNew(alphabetNavigationStyles, props.extendedStyles));
    const [alphabet] = useState(() => [{ letter: "all", count: 0, linkable: true }, ...props.alphabet]);
    const [selectedLetter, setSelectedLetter] = useState(props.selectedLetter);

    // XNG-Changes: To get selectedLetter change when a user click on clean Filter
    useEffect(() => {
        setSelectedLetter(props.selectedLetter);
    }, [props.selectedLetter]);

    const handleClickOfLetter = (letter: string) => {
        props.onLetterClick?.(letter);
    };

    // XNG-Changes
    const createHandleClickOfLetter = (letter: string) => () => {
        setSelectedLetter(letter);
        if (props.searchFilterBrands && props.handleSearchFilterBrands) {
            props.handleSearchFilterBrands(props.searchFilterBrands);
        }
        return handleClickOfLetter(letter);
    };

    // XNG-Changes: we add `All` as a first element
    if (alphabet.length <= 1) {
        return null;
    }

    // XNG-Changes
    const handleSelectChange = (e: any) => {
        setSelectedLetter(e.target.value);
        return handleClickOfLetter(e.target.value);
    };
    // XNG-Changes: Add filter, change from # to 0-9
    return (
        <StyledWrapper {...styles.container} data-test-selector="alphabetNavigation">
            <StyledWrapper {...styles.brandsLettersWrapper}>
                <StyledWrapper {...styles.alphabetSection}>
                    <Typography {...styles.titleText}>{translate("Filter Alphabetically")}</Typography>
                    <StyledWrapper {...styles.alphabetContainer}>
                        {alphabet.map(alphabetItem => (
                            <StyledSpan
                                {...styles.navigationItem}
                                key={alphabetItem.letter}
                                data-test-selector="alphabetNavigationItemLetter"
                            >
                                {alphabetItem.linkable ? (
                                    <Link
                                        {...styles.navigationLink}
                                        onClick={createHandleClickOfLetter(alphabetItem.letter)}
                                        aria-label={alphabetItem.letter}
                                        {...{
                                            selectedLetter:
                                                selectedLetter === alphabetItem.letter && alphabetItem.linkable,
                                            disabled: !alphabetItem.linkable,
                                        }}
                                    >
                                        {alphabetItem.letter === "#" ? translate("0-9") : alphabetItem.letter}
                                    </Link>
                                ) : (
                                    <Typography {...styles.navigationTextEmpty}>{alphabetItem.letter}</Typography>
                                )}
                            </StyledSpan>
                        ))}
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.selectWrapper}>
                    <Select
                        {...styles.select}
                        placeholder={translate("Search by First Letter")}
                        onChange={e => handleSelectChange(e)}
                        value={"" || selectedLetter}
                        label={translate("Filter Alphabetically")}
                    >
                        <option disabled value="">
                            {translate("Search by First Letter")}
                        </option>
                        {alphabet.map(alphabetItem => (
                            <option
                                disabled={!alphabetItem.linkable}
                                key={alphabetItem.letter}
                                value={alphabetItem.letter}
                            >
                                {alphabetItem.letter === "#"
                                    ? translate("0-9")
                                    : alphabetItem.letter === "all"
                                    ? translate("All")
                                    : alphabetItem.letter.toUpperCase()}
                            </option>
                        ))}
                    </Select>
                </StyledWrapper>
                <StyledWrapper {...styles.filterSection}>
                    <TextField
                        name="filterBrands"
                        value={props.searchFilterBrands}
                        placeholder={translate("Search by Name")}
                        onChange={e => props.handleSearchFilterBrands?.(e.target.value)}
                        {...styles.filterInput}
                        label={translate("Search by Name")}
                    />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default AlphabetNavigation;
